/**
 * DW_FEEDING_ENGINE
 * Single source of truth for the Dog & Whistle storefront feeding quiz.
 *
 * Verified against the real "Feeding Plan Calculator" admin tool on 2026-08-29
 * (constants confirmed directly from that tool's config). If the admin tool's
 * numbers ever change, this file must be manually re-synced -- there is no
 * live API link between the two systems.
 *
 * ONE EXCEPTION: the Puppy life-stage multiplier. The admin tool's own config
 * states a 2.0x-3.0x RER range for puppies but does not break that range into
 * Low/Moderate/High values the way Adult and Senior are broken down. Rather
 * than invent a number, this file interpolates evenly within D&W's own
 * confirmed 2.0-3.0 range, using the same even-step pattern the admin tool
 * already uses for Adult (1.4/1.6/1.8) and Senior (1.2/1.4/1.6). This is
 * standard, non-proprietary veterinary-textbook territory (puppies generally
 * run 2x-3x RER) -- not sourced from any competitor. Flag for confirmation
 * against the real admin tool when available.
 */
(function (global) {
  'use strict';

  var KCAL_PER_OZ = 39; // D&W portfolio-wide blended average (confirmed range 35.3-40.0 kcal/oz; no per-product density yet)
  var OZ_PER_POUCH = 8;

  var LIFE_STAGE_FACTORS = {
    puppy: { low: 2.0, moderate: 2.5, high: 3.0 }, // DW_INTERPOLATED -- see header comment
    adult: { low: 1.4, moderate: 1.6, high: 1.8 },
    senior: { low: 1.2, moderate: 1.4, high: 1.6 }
  };

  var PLAN_TIERS = [
    { key: 'puppy', label: 'Puppy Plan', price: 'Contact for pricing', minLb: 0, maxLb: Infinity, repLb: null, ageOverride: 'puppy' },
    { key: 'small', label: 'Small Dog Subscription', price: '$85 / 14 days', minLb: 0, maxLb: 25, repLb: 15 },
    { key: 'medium', label: 'Medium Dog Subscription', price: '$135 / 14 days', minLb: 25, maxLb: 50, repLb: 37 },
    { key: 'large', label: 'Large Dog Subscription', price: '$170 / 14 days', minLb: 50, maxLb: 75, repLb: 55 },
    { key: 'custom', label: 'Custom Plan', price: 'Contact for pricing', minLb: 75, maxLb: Infinity, repLb: null }
  ];

  function lbToKg(lb) {
    return lb / 2.20462;
  }

  function kgToLb(kg) {
    return kg * 2.20462;
  }

  function rer(weightLb) {
    var kg = lbToKg(weightLb);
    return 70 * Math.pow(kg, 0.75);
  }

  function lifeStageFactor(ageKey, activityKey) {
    var table = LIFE_STAGE_FACTORS[ageKey];
    if (!table) return null;
    return table[activityKey] || null;
  }

  function mer(weightLb, ageKey, activityKey) {
    var factor = lifeStageFactor(ageKey, activityKey);
    if (!factor) return null;
    return rer(weightLb) * factor;
  }

  function planForDog(weightLb, ageKey) {
    if (ageKey === 'puppy') {
      return PLAN_TIERS[0];
    }
    for (var i = 1; i < PLAN_TIERS.length; i++) {
      var tier = PLAN_TIERS[i];
      if (weightLb >= tier.minLb && weightLb < tier.maxLb) {
        return tier;
      }
    }
    return PLAN_TIERS[PLAN_TIERS.length - 1];
  }

  /**
   * Full calculation for one dog.
   * @param {Object} input
   * @param {number} input.weightLb
   * @param {'puppy'|'adult'|'senior'} input.age
   * @param {'low'|'moderate'|'high'} input.activity
   */
  function calculate(input) {
    var weightLb = input.weightLb;
    var age = input.age;
    var activity = input.activity;

    var dailyKcal = mer(weightLb, age, activity);
    if (dailyKcal === null) return null;

    var dailyOz = dailyKcal / KCAL_PER_OZ;
    var perMealOz = dailyOz / 2;
    var plan = planForDog(weightLb, age);

    return {
      dailyKcal: Math.round(dailyKcal),
      dailyOz: Math.round(dailyOz * 10) / 10,
      perMealOz: Math.round(perMealOz * 10) / 10,
      pouches: Math.round((dailyOz / OZ_PER_POUCH) * 10) / 10,
      plan: {
        key: plan.key,
        label: plan.label,
        price: plan.price
      }
    };
  }

  global.DWFeedingEngine = {
    KCAL_PER_OZ: KCAL_PER_OZ,
    OZ_PER_POUCH: OZ_PER_POUCH,
    LIFE_STAGE_FACTORS: LIFE_STAGE_FACTORS,
    PLAN_TIERS: PLAN_TIERS,
    lbToKg: lbToKg,
    kgToLb: kgToLb,
    rer: rer,
    mer: mer,
    planForDog: planForDog,
    calculate: calculate
  };
})(window);
